// js/tradeControl.js
// Lógica principal y coordinación del Trade Control

class TradeControl {
    constructor() {
        this.currentScore = null;
        this.currentRisk = 1.0;
        this.todayTradeCount = 0;
        this.todayPnL = 0;
        this.dailyTarget = 200;
        this.maxTrades = 3;
        this.aiAnalysis = null;
        this.installationCache = {};
    }

    /**
     * Inicializa el Trade Control
     */
    async init() {
        try {
            await this.loadConfig();
            await this.updateTodayStats();
            this.calculateCurrentScore();
        } catch (error) {

        }
    }

    /**
     * Carga configuración del challenge
     */
    async loadConfig() {
        try {
            const accountId = window.currentAccount;
            if (!accountId) return;

            let hasPlanTrades = false;
            if (window.planManagerV2) {
                const plan = await window.planManagerV2.getPlanCached();
                const planTrades = plan?.max_trades_day_value;
                if (planTrades !== undefined && planTrades !== null && !isNaN(planTrades)) {
                    this.maxTrades = Math.max(1, Number(planTrades));
                    hasPlanTrades = true;
                }
            }

            const key = `TC_SESSION_CONFIG_${accountId}`;
            const stored = localStorage.getItem(key);

            if (stored) {
                const config = JSON.parse(stored);
                this.dailyTarget = config.dailyTarget || 200;
                if (!hasPlanTrades) {
                    this.maxTrades = config.maxTrades || 3;
                }
            }
        } catch (error) {

        }
    }

    /**
     * Guarda configuración de sesión
     */
    saveConfig() {
        try {
            const accountId = window.currentAccount;
            if (!accountId) return;

            const key = `TC_SESSION_CONFIG_${accountId}`;
            const config = {
                dailyTarget: this.dailyTarget,
                maxTrades: this.maxTrades
            };
            localStorage.setItem(key, JSON.stringify(config));
        } catch (error) {

        }
    }

    /**
     * Establece objetivo diario
     */
    setDailyTarget(value) {
        this.dailyTarget = Math.max(0, value);
        this.saveConfig();
    }

    /**
     * Establece máximo de trades
     */
    setMaxTrades(value) {
        this.maxTrades = Math.max(1, Math.min(10, value));
        this.saveConfig();
    }

    /**
     * Actualiza estadísticas del día
     */
    async updateTodayStats() {
        try {
            const turso = window.tursoClient;
            const accountId = window.currentAccount;
            
            if (!turso || !accountId) {
                this.todayTradeCount = 0;
                this.todayPnL = 0;
                return;
            }

            // Usar fecha local en lugar de UTC
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const today = `${year}-${month}-${day}`;

            const result = await turso.execute({
                sql: `
                    SELECT 
                        COUNT(*) as count,
                        SUM(profit_usd) as pnl
                    FROM trades
                    WHERE account_id = ?
                        AND REPLACE(SUBSTR(date, 1, 10), '.', '-') = ?
                `,
                args: [accountId, today]
            });

            if (result?.rows?.[0]) {
                this.todayTradeCount = parseInt(result.rows[0].count) || 0;
                this.todayPnL = parseFloat(result.rows[0].pnl) || 0;
            } else {
                this.todayTradeCount = 0;
                this.todayPnL = 0;
            }

        } catch (error) {
            this.todayTradeCount = 0;
            this.todayPnL = 0;
        }
    }

    /**
     * Calcula el score actual
     */
    async calculateCurrentScore() {
        try {
            const economicEvents = await this.getEconomicEvents();
            const dayStats = await this.getDayOfWeekStats();
            const streak = await this.getStreak();
            const emotionalState = await this.getEmotionalState();

            const score = this.computeScore({
                economicEvents,
                dayStats,
                streak,
                emotionalState
            });

            this.currentScore = score;
            return score;

        } catch (error) {

            return {
                score: 50,
                status: 'CAUTION',
                message: 'Error al calcular condiciones'
            };
        }
    }

    /**
     * Computa el score basado en múltiples factores
     */
    computeScore({ economicEvents, dayStats, streak, emotionalState }) {
        const weights = {
            news: 0.30,
            dayOfWeek: 0.30,
            streak: 0.20,
            emotional: 0.12,
            timeOfDay: 0.08
        };

        // Factor 1: Noticias (30%)
        const hasCritical = (economicEvents || []).some(ev => ev.impact === 'CRITICAL');
        const hasHigh = (economicEvents || []).some(ev => ev.impact === 'HIGH');
        let newsScore = 100;
        if (hasCritical) newsScore = 15;
        else if (hasHigh) newsScore = 40;

        // Factor 2: Día de la semana (30%)
        let dayScore = 50;
        if (dayStats) {
            const diff = dayStats.winRate - dayStats.avgWR;
            if (diff >= 10) dayScore = 100;
            else if (diff >= 5) dayScore = 85;
            else if (diff >= 0) dayScore = 70;
            else if (diff >= -5) dayScore = 50;
            else dayScore = 30;
        }

        // Factor 3: Racha (20%)
        let streakScore = 50;
        if (streak) {
            if (streak.current === 'WIN') {
                streakScore = Math.min(70 + (streak.consecutive * 8), 100);
            } else {
                streakScore = Math.max(70 - (streak.consecutive * 12), 20);
            }
        }

        // Factor 4: Estado emocional (12%)
        const emotionalMap = {
            'calm': 90,
            'focused': 95,
            'confident': 85,
            'neutral': 70,
            'anxious': 40,
            'frustrated': 30,
            'angry': 20
        };
        const emotionalScore = emotionalState ? (emotionalMap[emotionalState] || 70) : 70;

        // Factor 5: Horario (8%)
        const hour = new Date().getHours();
        let timeScore = 50;
        if (hour >= 9 && hour < 11) timeScore = 90;
        else if (hour >= 14 && hour < 16) timeScore = 85;
        else if (hour >= 11 && hour < 14) timeScore = 70;

        // Calcular score total
        const totalScore = Math.round(
            (newsScore * weights.news) +
            (dayScore * weights.dayOfWeek) +
            (streakScore * weights.streak) +
            (emotionalScore * weights.emotional) +
            (timeScore * weights.timeOfDay)
        );

        let status = 'CAUTION';
        let message = '⚠️ Condiciones Moderadas';

        if (totalScore >= 70) {
            status = 'SAFE';
            message = '✅ Condiciones Favorables';
        } else if (totalScore < 50) {
            status = 'DANGER';
            message = '🚫 Condiciones Desfavorables';
        }

        return {
            score: totalScore,
            status,
            message
        };
    }

    /**
     * Obtiene eventos económicos del día
     */
    async getEconomicEvents() {
        try {
            // Intentar cargar desde cache
            const cached = localStorage.getItem('economicEvents_v1');
            if (cached) {
                return JSON.parse(cached);
            }

            // Fallback: eventos predeterminados
            return this.getFallbackEconomicEvents();

        } catch (error) {

            return [];
        }
    }

    /**
     * Eventos económicos de fallback
     */
    getFallbackEconomicEvents() {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const dayOfMonth = today.getDate();
        const month = today.getMonth();
        const year = today.getFullYear();
        
        let events = [];
        
        // Detectar primer viernes del mes (NFP)
        let firstFriday = null;
        for (let d = 1; d <= 7; d++) {
            const testDate = new Date(year, month, d);
            if (testDate.getDay() === 5) {
                firstFriday = d;
                break;
            }
        }
        
        if (dayOfMonth === firstFriday && dayOfWeek === 5) {
            events.push({
                title: '🚨 NFP - Non-Farm Payroll',
                impact: 'CRITICAL',
                time: '13:30 UTC',
                currency: 'USD',
                description: 'EVENTO CRÍTICO - NO OPERAR'
            });
        }
        
        // Eventos por día de la semana
        switch (dayOfWeek) {
            case 1: // Lunes
                events.push({
                    title: 'ISM Manufacturing Index',
                    impact: 'HIGH',
                    time: '15:00 UTC',
                    currency: 'USD',
                    description: 'PMI manufacturero USA'
                });
                break;
            case 2: // Martes
                events.push({
                    title: 'IPC - Índice de Precios Consumidor',
                    impact: 'CRITICAL',
                    time: '13:30 UTC',
                    currency: 'USD',
                    description: 'CPI - Inflación mensual'
                });
                break;
            case 3: // Miércoles
                events.push({
                    title: 'Decisión de Tasa FOMC',
                    impact: 'CRITICAL',
                    time: '18:00 UTC',
                    currency: 'USD',
                    description: 'Política monetaria USA'
                });
                break;
            case 4: // Jueves
                events.push({
                    title: 'Jobless Claims',
                    impact: 'HIGH',
                    time: '13:30 UTC',
                    currency: 'USD',
                    description: 'Solicitudes de desempleo'
                });
                break;
            case 5: // Viernes
                events.push({
                    title: 'Core Retail Sales',
                    impact: 'HIGH',
                    time: '13:30 UTC',
                    currency: 'USD',
                    description: 'Ventas minoristas'
                });
                break;
        }
        
        return events;
    }

    /**
     * Obtiene estadísticas del día de la semana
     */
    async getDayOfWeekStats() {
        try {
            const turso = window.tursoClient;
            const accountId = window.currentAccount;
            
            if (!turso || !accountId) return null;

            const dayOfWeek = new Date().getDay();

            const result = await turso.execute({
                sql: `
                    SELECT 
                        COUNT(*) as total,
                        SUM(CASE WHEN profit_usd > 0 THEN 1 ELSE 0 END) * 100.0 / COUNT(*) as wr
                    FROM trades
                    WHERE account_id = ?
                        AND CAST(strftime('%w', date) AS INTEGER) = ?
                        AND date >= date('now', '-56 days')
                `,
                args: [accountId, dayOfWeek]
            });

            const avgResult = await turso.execute({
                sql: `
                    SELECT 
                        SUM(CASE WHEN profit_usd > 0 THEN 1 ELSE 0 END) * 100.0 / COUNT(*) as avg_wr
                    FROM trades
                    WHERE account_id = ?
                        AND date >= date('now', '-90 days')
                `,
                args: [accountId]
            });

            return {
                winRate: parseFloat(result?.rows?.[0]?.wr) || 0,
                avgWR: parseFloat(avgResult?.rows?.[0]?.avg_wr) || 0,
                totalTrades: parseInt(result?.rows?.[0]?.total) || 0
            };

        } catch (error) {

            return null;
        }
    }

    /**
     * Obtiene la racha actual
     */
    async getStreak() {
        try {
            const turso = window.tursoClient;
            const accountId = window.currentAccount;
            
            if (!turso || !accountId) return null;

            const result = await turso.execute({
                sql: `
                    SELECT profit_usd
                    FROM trades
                    WHERE account_id = ?
                    ORDER BY id DESC
                    LIMIT 5
                `,
                args: [accountId]
            });

            if (!result?.rows || result.rows.length === 0) {
                return { current: 'NEUTRAL', consecutive: 0 };
            }

            let consecutive = 0;
            let current = null;

            for (const trade of result.rows) {
                const isWin = parseFloat(trade.profit_usd) > 0;
                
                if (current === null) {
                    current = isWin ? 'WIN' : 'LOSS';
                    consecutive = 1;
                } else if ((current === 'WIN' && isWin) || (current === 'LOSS' && !isWin)) {
                    consecutive++;
                } else {
                    break;
                }
            }

            return { current, consecutive };

        } catch (error) {

            return { current: 'NEUTRAL', consecutive: 0 };
        }
    }

    /**
     * Obtiene el estado emocional de hoy
     */
    async getEmotionalState() {
        try {
            const turso = window.tursoClient;
            const accountId = window.currentAccount;
            const userId = window.currentUserId || sessionStorage.getItem('currentUserId') || localStorage.getItem('currentUserId');
            
            if (!turso || !accountId || !userId) return null;

            const today = new Date().toISOString().split('T')[0];

            const result = await turso.execute({
                sql: `
                    SELECT sentiment
                    FROM daily_notes
                    WHERE user_id = ?
                        AND account_id = ?
                        AND date = ?
                    LIMIT 1
                `,
                args: [userId, accountId, today]
            });

            return result?.rows?.[0]?.sentiment || null;

        } catch (error) {

            return null;
        }
    }

    /**
     * Calcula el riesgo actual basado en reglas
     */
    async calculateCurrentRisk() {
        try {
            const config = this.loadChallengeConfig();
            const yesterday = await this.getYesterdayResult();
            const todayCount = this.todayTradeCount;
            const economicEvents = await this.getEconomicEvents();

            // Reglas base por tipo de cuenta
            let riskSchedule = [1.0, 0.75, 0.5];

            if (config.accountType === 'funded') {
                riskSchedule = [1.0, 1.0, 1.0];
            } else if (yesterday?.result === 'win' && yesterday?.rValue > 2) {
                if (config.accountType === 'f2') {
                    riskSchedule = [1.25, 0.75, 0.5];
                } else {
                    riskSchedule = [1.5, 1.0, 0.75];
                }
            }

            let risk = riskSchedule[Math.min(todayCount, riskSchedule.length - 1)];

            // Reducir por noticias críticas
            const hasCritical = economicEvents.some(ev => ev.impact === 'CRITICAL');
            const hasHigh = economicEvents.some(ev => ev.impact === 'HIGH');

            if (hasCritical || hasHigh) {
                risk = Math.min(risk, 0.5);
            }

            this.currentRisk = risk;
            return risk;

        } catch (error) {

            return 1.0;
        }
    }

    /**
     * Obtiene resultado de ayer
     */
    async getYesterdayResult() {
        try {
            const turso = window.tursoClient;
            const accountId = window.currentAccount;
            
            if (!turso || !accountId) return null;

            const today = new Date().toISOString().split('T')[0];

            const result = await turso.execute({
                sql: `
                    SELECT 
                        date,
                        SUM(profit_usd) as pnl
                    FROM trades
                    WHERE account_id = ?
                        AND date < ?
                    GROUP BY date
                    ORDER BY date DESC
                    LIMIT 1
                `,
                args: [accountId, today]
            });

            if (!result?.rows?.[0]) return null;

            const pnl = parseFloat(result.rows[0].pnl);
            
            return {
                result: pnl > 0 ? 'win' : (pnl < 0 ? 'loss' : 'breakeven'),
                rValue: Math.abs(pnl)
            };

        } catch (error) {

            return null;
        }
    }

    /**
     * Carga configuración del challenge
     */
    loadChallengeConfig() {
        try {
            const accountId = window.currentAccount;
            if (!accountId) return { accountType: 'f1' };

            const key = `MC_CHALLENGE_CONFIG_${accountId}`;
            const stored = localStorage.getItem(key);

            if (stored) {
                return JSON.parse(stored);
            }

            return { accountType: 'f1' };

        } catch (error) {

            return { accountType: 'f1' };
        }
    }

    /**
     * Ejecuta análisis IA completo
     */
    async runAIAnalysis() {
        try {
            // Intentar cargar desde cache primero (con await)
            const cached = await window.tradeControlAI?.loadCachedAnalysis();
            if (cached) {
                this.aiAnalysis = cached;
                return cached;
            }

            // Ejecutar nuevo análisis con timeout de 15 segundos
            const analysisPromise = window.tradeControlAI?.runAnalysis();
            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Análisis timeout')), 15000)
            );
            
            const analysis = await Promise.race([analysisPromise, timeoutPromise]);
            this.aiAnalysis = analysis;
            return analysis;

        } catch (error) {

            // Retornar análisis fallback
            const fallback = window.tradeControlAI?.getFallbackAnalysis();
            this.aiAnalysis = fallback;
            return fallback;
        }
    }

    /**
     * Getters públicos
     */
    getLatestScore() {
        return this.currentScore || { score: 0, status: 'CAUTION', message: 'Calculando...' };
    }

    getCurrentRisk() {
        return this.currentRisk;
    }

    normalizeNumeric(value, fallback = 0) {
        if (value === null || value === undefined) return fallback;
        if (typeof value === 'number') return isNaN(value) ? fallback : value;
        const cleaned = String(value).replace(/[^0-9.-]/g, '');
        if (!cleaned || cleaned === '-' || cleaned === '.') return fallback;
        const parsed = Number(cleaned);
        return isNaN(parsed) ? fallback : parsed;
    }

    async getTodayTradeCount() {
        await this.updateTodayStats();
        return this.todayTradeCount;
    }

    async getTodayPnL() {
        await this.updateTodayStats();
        return this.todayPnL;
    }

    async getDailyTarget() {
        // 🆕 Obtener objetivo del plan activo
        try {
            if (window.planManagerV2) {
                const plan = await window.planManagerV2.getPlanCached();
                if (plan && plan.profit_goal_value) {
                    return this.normalizeNumeric(plan.profit_goal_value, this.dailyTarget);
                }
            }
        } catch (e) {
        }
        
        // Fallback al valor configurado localmente
        return this.dailyTarget;
    }

    getMaxTrades() {
        return this.maxTrades;
    }

    async getMaxTradesAsync() {
        try {
            if (window.planManagerV2) {
                const plan = await window.planManagerV2.getPlanCached();
                const planValue = plan?.max_trades_day_value;
                if (planValue !== undefined && planValue !== null) {
                    const parsed = this.normalizeNumeric(planValue, NaN);
                    if (!isNaN(parsed)) {
                        this.maxTrades = Math.max(1, Math.floor(parsed));
                    }
                    return this.maxTrades;
                }
            }
        } catch (e) {
        }
        return this.maxTrades;
    }

    async getMaxDailyLoss() {
        // 🆕 Obtener pérdida máxima del plan activo
        try {
            if (window.planManagerV2) {
                const plan = await window.planManagerV2.getPlanCached();
                if (plan && plan.max_daily_loss_value) {
                    return this.normalizeNumeric(plan.max_daily_loss_value, 100);
                }
            }
        } catch (e) {
        }
        
        // Fallback por defecto
        return 100;
    }

    // ============================================
    // 🆕 AUTO-MANAGEMENT (EA) HELPERS
    // ============================================
    async getCurrentInstallationId(accountIdOverride = null) {
        const accountId = accountIdOverride || window.currentAccount;
        if (!accountId) return null;

        if (this.installationCache[accountId]) {
            return this.installationCache[accountId];
        }

        const turso = window.tursoClient;
        if (!turso) return null;

        const account = window.accounts?.[accountId] || {};
        const mtAccountNumber = account.mt_account_number ? String(account.mt_account_number) : '';

        const userId = window.currentUserId || sessionStorage.getItem('tfx_userId') || sessionStorage.getItem('currentUserId') || localStorage.getItem('tfx_userId') || localStorage.getItem('currentUserId') || '';
        const userEmail = sessionStorage.getItem('userEmail') || localStorage.getItem('userEmail') || '';

        try {
            const result = await turso.execute({
                sql: `SELECT installation_id
                      FROM installations
                      WHERE (account_id = ? OR account = ?)
                        AND (? = '' OR user_id = ? OR user_email = ?)
                      ORDER BY created_at DESC
                      LIMIT 1`,
                args: [accountId, mtAccountNumber, userId, userId, userEmail]
            });

            const installationId = result?.rows?.[0]?.installation_id || null;
            if (installationId) {
                this.installationCache[accountId] = installationId;
            }
            return installationId;
        } catch (e) {
            return null;
        }
    }

    getSessionLabelByTime() {
        const hour = new Date().getHours();
        if (hour >= 6 && hour < 12) return 'morning';
        if (hour >= 12 && hour < 18) return 'afternoon';
        return 'night';
    }

    async buildAutoManagementConfig() {
        const profitTarget = await this.getDailyTarget();
        const maxDailyLoss = await this.getMaxDailyLoss();
        const maxTrades = await this.getMaxTradesAsync();

        return {
            profit_target: this.normalizeNumeric(profitTarget, 0),
            max_daily_loss: this.normalizeNumeric(maxDailyLoss, 0),
            max_trades: Math.max(0, Math.floor(this.normalizeNumeric(maxTrades, 0))),
            session: this.getSessionLabelByTime(),
            pairs: [],
            risk_level: this.currentRisk > 1.2 ? 'high' : 'medium'
        };
    }

    async activateAutoManagementSession() {
        if (window.getUserAccess) {
            const { userPlan, userType } = window.getUserAccess();
            const plan = String(userPlan || '').toLowerCase().trim();
            const type = String(userType || '').toLowerCase().trim();
            if (!(type === 'admin' || plan === 'unlimited')) {
                throw new Error('Tu plan no permite la gestión automática.');
            }
        }
        if (window.planManagerV2 && typeof window.planManagerV2.getPlanCached === 'function') {
            const plan = await window.planManagerV2.getPlanCached();
            if (!plan) {
                throw new Error('No hay plan activo para esta cuenta.');
            }
        }
        const installationId = await this.getCurrentInstallationId();
        if (!installationId) {
            throw new Error('No se encontró la instalación MT para esta cuenta.');
        }

        const config = await this.buildAutoManagementConfig();
        const baseUrl = (typeof window !== 'undefined' && window.WORKER_URL) ? window.WORKER_URL : '';
        const response = await fetch(`${baseUrl}/api/activate-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                installation_id: installationId,
                config
            })
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'No se pudo activar la sesión');
        }

        return { installationId, config };
    }

    async deactivateAutoManagementSession(accountIdOverride = null) {
        let installationId = await this.getCurrentInstallationId(accountIdOverride);
        if (!installationId) {
            const accountId = accountIdOverride || window.currentAccount;
            const cached = accountId ? this.installationCache[accountId] : null;
            if (cached) {
                installationId = cached;
            }
        }
        if (!installationId) {
            return { skipped: true };
        }

        const baseUrl = (typeof window !== 'undefined' && window.WORKER_URL) ? window.WORKER_URL : '';
        const response = await fetch(`${baseUrl}/api/deactivate-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ installation_id: installationId })
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || `No se pudo desactivar la sesión (${response.status})`);
        }

        return { installationId };
    }

    /**
     * Obtiene todos los trades del día actual
     */
    async getTodayTrades() {
        try {
            const turso = window.tursoClient;
            const accountId = window.currentAccount;
            
            if (!turso || !accountId) return [];

            // Usar fecha local
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const today = `${year}-${month}-${day}`;

            const result = await turso.execute({
                sql: `
                    SELECT 
                        id, pair, direction, lot_size, profit_usd, profitUSD,
                        entry_time, exit_time, comment, timestamp
                    FROM trades
                    WHERE account_id = ?
                        AND REPLACE(SUBSTR(date, 1, 10), '.', '-') = ?
                    ORDER BY timestamp DESC
                `,
                args: [accountId, today]
            });

            return result?.rows || [];
        } catch (error) {
            return [];
        }
    }

    async getAIAnalysis() {
        if (this.aiAnalysis) {
            return this.aiAnalysis;
        }
        return await this.runAIAnalysis();
    }

    /**
     * Refresca todos los datos
     */
    async refresh() {
        await this.init();
        await this.calculateCurrentRisk();
    }
}

// Inicializar globalmente
if (typeof window !== 'undefined') {
    window.tradeControl = new TradeControl();
    
    // Auto-inicializar cuando haya cuenta seleccionada
    document.addEventListener('DOMContentLoaded', () => {
        if (window.currentAccount) {
            window.tradeControl.init();
        }
    });

    // Escuchar cambios de cuenta
    const originalAccountSelect = window.accountSelect;
    if (originalAccountSelect) {
        originalAccountSelect.addEventListener('change', () => {
            setTimeout(() => {
                window.tradeControl.init();
            }, 500);
        });
    }
}
